import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  addResourceType,
  updateResourceType,
  type ResourceTypeEntity,
} from '../api/resourceService';
import { useTranslation } from 'react-i18next';

export default function ResourceTypeForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: ResourceTypeEntity;
  onSaved?: (rt: ResourceTypeEntity) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  
  type FormValues = {
    name_en: string;
    name_es: string;
  };

  const { register, handleSubmit, reset, formState: { errors }, watch } = useForm<FormValues>({
    defaultValues: initial
      ? {
        name_en: initial.name.en,
        name_es: initial.name.es,
      }
      : {
        name_en: '',
        name_es: '',
      },
  });

  const watchedNameEn = watch('name_en');
  const watchedNameEs = watch('name_es');

  useEffect(() => {
    if (initial) {
      reset({ name_en: initial.name.en, name_es: initial.name.es });
    }
  }, [initial, reset]);

  const onSubmit = handleSubmit(async (values: FormValues) => {
    const payload = { name: { en: values.name_en, es: values.name_es } };
    const saved = initial
      ? await updateResourceType({ id: initial.id, ...payload })
      : await addResourceType(payload);
    onSaved?.(saved);
  });

  return (
    <div className="w-full max-w-3xl mx-auto max-h-[40vh] flex flex-col">
      {/* Header del formulario - STICKY ARRIBA */}
      <div className="sticky top-0 bg-[var(--surface)] z-10 p-6 pb-4 border-b border-[var(--border)]">
        <h3 className="text-3xl font-bold text-[var(--text-default)] mb-2">
          {initial ? t('resourceTypeForm.editTitle') : t('resourceTypeForm.title')}
        </h3>
        <p className="text-[var(--text-muted)]">
          {initial ? t('resourceTypeForm.editSubtitle') : t('resourceTypeForm.subtitle')}
        </p>
      </div>

      {/* Formulario */}
      <div className="p-6">
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Campos de nombre en inglés y español */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--text-default)]">
                {t('resourceTypeForm.nameEn')}
              </label>
              <div className="relative">
                <input
                  {...register('name_en', { 
                    required: t('resourceTypeForm.validation.nameEnRequired'),
                    maxLength: { value: 30, message: t('resourceTypeForm.validation.maxLength', { count: 30 }) }
                  })}
                  placeholder={t('resourceTypeForm.nameEnPlaceholder')}
                  maxLength={30}
                  className="w-full px-4 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-[var(--text-muted)]">
                  {watchedNameEn?.length || 0}/30
                </div>
              </div>
              {errors.name_en && (
                <p className="text-[var(--text-muted)] text-sm mt-1">{errors.name_en.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--text-default)]">
                {t('resourceTypeForm.nameEs')}
              </label>
              <div className="relative">
                <input
                  {...register('name_es', { 
                    required: t('resourceTypeForm.validation.nameEsRequired'),
                    maxLength: { value: 30, message: t('resourceTypeForm.validation.maxLength', { count: 30 }) }
                  })}
                  placeholder={t('resourceTypeForm.nameEsPlaceholder')}
                  maxLength={30}
                  className="w-full px-4 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-[var(--text-muted)]">
                  {watchedNameEs?.length || 0}/30
                </div>
              </div>
              {errors.name_es && (
                <p className="text-[var(--text-muted)] text-sm mt-1">{errors.name_es.message}</p>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Footer con botones - STICKY ABAJO */}
      <div className="sticky bottom-0 bg-[var(--surface)] z-10 p-6 pt-4 border-t border-[var(--border)]">
        <div className="flex justify-start gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors duration-200 font-medium"
          >
            {t('resourceTypeForm.actions.cancel')}
          </button>
          <button
            type="submit"
            onClick={onSubmit}
            className="px-6 py-3 bg-[var(--text-default)] text-[var(--surface)] rounded-lg hover:bg-[var(--border)] transition-colors duration-200 font-medium"
          >
            {initial ? t('resourceTypeForm.actions.update') : t('resourceTypeForm.actions.create')}
          </button>
        </div>
      </div>
    </div>
  );
} 